import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Users as UsersIcon, Plus, Trash2, Shield, Copy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";

const ROLE_LABELS = { admin: "Admin", staff: "Staff", referral_partner: "Referral Partner" };
const ROLE_STYLES = {
  admin: "bg-slate-900 text-white border-slate-900",
  staff: "bg-blue-100 text-blue-800 border-blue-200",
  referral_partner: "bg-amber-100 text-amber-800 border-amber-200",
};

const genPassword = () => {
  const c = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  return Array.from({ length: 12 }, () => c[Math.floor(Math.random() * c.length)]).join("");
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "referral_partner", organization: "", password: genPassword(), send_invite: true });
  const [saving, setSaving] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setForm({ name: "", email: "", role: "referral_partner", organization: "", password: genPassword(), send_invite: true });
    setOpen(true);
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password are required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/users`, { ...form, login_url: `${window.location.origin}/login` });
      toast.success(form.send_invite ? `User created and invite emailed to ${form.email}.` : `User created. Share the password with ${form.name}.`);
      setOpen(false);
      fetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (id, role) => {
    try { await axios.put(`${API}/users/${id}`, { role }); toast.success("Role updated"); fetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try { await axios.put(`${API}/users/${u.id}`, { active: !u.active }); fetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    try { await axios.delete(`${API}/users/${id}`); toast.success("User deleted"); fetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>User Management</h1>
          <p className="text-slate-500 mt-1">Create accounts for staff and referral partners and manage their roles</p>
        </div>
        <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white gap-2" data-testid="add-user-btn">
          <Plus className="w-4 h-4" /> Add User
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><UsersIcon className="w-5 h-5 text-amber-600" />Accounts</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-slate-500 py-8 text-center">Loading...</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Organization</TableHead>
                  <TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.organization || "—"}</TableCell>
                    <TableCell>
                      {u.role === "admin" ? (
                        <Badge variant="outline" className={ROLE_STYLES.admin}><Shield className="w-3 h-3 mr-1" />Admin</Badge>
                      ) : (
                        <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                          <SelectTrigger className="w-40 h-8" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="staff">Staff</SelectItem>
                            <SelectItem value="referral_partner">Referral Partner</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      <button onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}>
                        <Badge variant="outline" className={u.active ? "bg-green-100 text-green-800 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                          {u.active ? "Active" : "Disabled"}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => remove(u.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>Add New User</DialogTitle>
            <DialogDescription>Create an account. Share the generated password securely with the new user.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Full Name *</Label><Input className="mt-1" data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email *</Label><Input type="email" className="mt-1" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Organization</Label><Input className="mt-1" data-testid="user-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="e.g., Pikes Peak Workforce Center" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="referral_partner">Referral Partner</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Temporary Password *</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <Button type="button" variant="outline" onClick={() => setForm({ ...form, password: genPassword() })}>Regenerate</Button>
                <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(form.password); toast.success("Password copied"); }}><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="send-invite-toggle">
              <Checkbox checked={form.send_invite} onCheckedChange={(v) => setForm({ ...form, send_invite: !!v })} />
              <span className="text-sm text-slate-700">Email login invite with credentials to the user</span>
            </label>
            <Button onClick={create} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-user-btn">
              {saving ? "Creating..." : "Create User"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
